<template>
  <l-map ref="clusterMap" :zoom="simple.zoom" :options="{ zoomControl: false }" :center="simple.center" :bounds="bounds"
    @ready="onMapReady" :style="{
      width: '100%',
      maxWidth: '100%',
      height: isFullscreen ? '100%' : '100%',
      boxShadow: '0px 3px 6px #bfc5db14',
      zIndex: 1,
      borderRadius: '8px',
    }">
    <l-tile-layer :url="simple.url" :attribution="simple.attribution" :subdomains="simple.subdomains"
      :max-zoom="simple.maxZoom"></l-tile-layer>

    <!-- Map Controls -->
    <l-control class="custom-control">
      <div class="flex flex-col items-center justify-center">
        <div class="flex flex-col items-center justify-center custom-control-zoom">
          <img @click="setZoom('add')" alt="Zoom In" title="Zoom In" data-tooltip-target
            src="@/assets/images/icon/zoom-plus.png" />
          <img @click="setZoom('min')" alt="Zoom Out" title="Zoom Out" data-tooltip-target
            src="@/assets/images/icon/zoom-min.png" />
        </div>

        <div class="flex flex-col items-center justify-center custom-control-fullscreen">
          <img @click="onMinimize" alt="Minimize Map" title="Minimize Map" v-if="isFullscreen"
            src="@/assets/images/icon/minimize.png" />
          <img @click="onFullscreen" alt="Expand Map" title="Expand Map" v-else
            src="@/assets/images/icon/fullscreen.png" />
        </div>
      </div>
    </l-control>

    <!-- Legend -->
    <l-control position="bottomleft" class="custom-legend p-[20px] w-[318px] h-auto bg-white rounded-[8px] gap-[12px]"
      v-if="isFullscreen">
      <div class="w-full h-auto grid justify-between grid-cols-12 items-start">
        <div class="col-span-10">
          <p class="!text-[16px] m-0">Legend</p>
        </div>
        <div class="col-span-2 text-right">
          <i class="fa fa-chevron-down cursor-pointer" aria-hidden="true" v-if="collapse == false"
            @click="collapse = true"></i>
          <i class="fa fa-chevron-up cursor-pointer" aria-hidden="true" v-if="collapse" @click="collapse = false"></i>
        </div>
      </div>

      <div v-if="!collapse" class="w-full grid gap-[10px]">
        <div class="custom-legend-item flex flex-row items-center justify-between" v-for="item in legends"
          :key="item.label">
          <input type="checkbox" class="styled-radio-checkbox mr-2" :id="`legend-${item.label}`" v-model="item.checked"
            @change="onLegendToggle(item)" />
          <img :src="markerIcon(item.icon)" class="custom-legend-icon !w-[20px] !h-[25px]" />
          <span class="mx-2 w-[150px] text-[#ABABAB] text-[16px]">{{ item.label }}</span>
          <span class="text-[#ABABAB] text-[16px]">{{ item.value !== null ? `(${item.value})` : "N/A" }}</span>
        </div>
      </div>
    </l-control>

    <!-- Markers -->
    <l-marker v-for="(item, index) in markers.filter((m) => m.show)"
      :key="`${index}_${item.latLng.lat}_${item.latLng.lng}`" :lat-lng="item.latLng"
      @click="!this.disableRedirect && innerClick(item)">
      <l-tooltip v-if="item.name !== ''">
        <div>{{ item.name }}</div>
      </l-tooltip>
      <l-icon :icon-size="sizeIcon(item.icon)" :icon-anchor="dynamicAnchor" :icon-url="markerIcon(item.icon)" />
    </l-marker>
  </l-map>
</template>
<script>
import { Tooltip } from "bootstrap/dist/js/bootstrap.esm.min.js";
import "leaflet/dist/leaflet.css";
import { latLngBounds } from "leaflet";
import {
  LMap,
  LMarker,
  LTileLayer,
  LTooltip,
  LIcon,
  LControl,
} from "@vue-leaflet/vue-leaflet";
export default {
  props: {
    legends: {
      type: Array,
      required: true,
    },
    isFullscreen: {
      type: Boolean,
      required: false,
      default: false,
    },
    markers: {
      type: Array,
      required: true,
      default: () => [],
      validator: function (value) {
        return value.every(
          (marker) => !isNaN(marker.lat) && !isNaN(marker.long)
        );
      },
    },
    disableRedirect: {
      type: Boolean,
      required: false,
    }
  },
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    LIcon,
    LControl,
  },
  watch: {
    markers: {
      immediate: true,
      handler() {
        if (this.mapReady) {
          this.fitMapToMarkers();
        }
      },
      deep: true,
    },
  },
  computed: {
    dynamicAnchor() {
      return [this.iconSquareSize / 2, this.iconSquareSize];
    },
  },
  data() {
    return {
      collapse: true,
      iconWidthSize: 16,
      iconHightSize: 20,
      iconSquareSize: 34,
      LightsOffline: require("../../../assets/images/icon/LightsOffline.png"),
      LuxOffline: require("../../../assets/images/icon/LuxOffline.png"),
      GatewayOffline: require("../../../assets/images/icon/GatewayOffline.png"),
      FeederPillar: require("../../../assets/images/icon/FeederPillar.png"),
      GatewayOnline: require("../../../assets/images/icon/GatewayOnline.png"),
      LuxOnline: require("../../../assets/images/icon/LuxOnline.png"),
      LightsOnlineOff: require("../../../assets/images/icon/LightsOnlineOff.png"),
      LightsOnlineOn: require("../../../assets/images/icon/LightsOnlineOn.png"),
      LightsOnMaintenance: require("../../../assets/images/icon/OnMaintenance.png"),
      bounds: latLngBounds(
        { lat: 2.9284, lng: 101.68424 },
        { lat: 2.928236, lng: 101.48424 }
      ),
      mapReady: false,
      simple: {
        url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        zoom: 15,
        minZoom: 3,
        maxZoom: 20,
        center: [2.928236, 101.68424],
        marker: [3.140853, 101.693207],
      },
      // legends: [],
    };
  },
  methods: {
    onLegendToggle(item) {
      for (var i = 0; i < this.markers.length; i++) {
        if (item.icon == this.markers[i].icon) {
          this.markers[i].show = item.checked;
        }
      }
    },
    onFullscreen() {
      console.log("CLUSTER MAP: Fullscreen button clicked, emitting 'on-fullscreen'");
      this.$emit("on-fullscreen");
    },
    onMinimize() {
      this.$emit("on-minimize");
    },
    markerIcon(key) {
      switch (key) {
        case "LightsOffline":
          return this.LightsOffline;
        case "LuxOffline":
          return this.LuxOffline;
        case "GatewayOffline":
          return this.GatewayOffline;
        case "FeederPillar":
          return this.FeederPillar;
        case "GatewayOnline":
          return this.GatewayOnline;
        case "LuxOnline":
          return this.LuxOnline;
        case "LightsOnlineOff":
          return this.LightsOnlineOff;
        case "LightsOnlineOn":
          return this.LightsOnlineOn;
        case "LightsOnMaintenance":
          return this.LightsOnMaintenance;

        default:
          break;
      }
    },
    sizeIcon(key) {
      switch (key) {
        case "LightsOffline":
          return [this.iconWidthSize, this.iconHightSize];
        case "LightsOnlineOff":
          return [this.iconWidthSize, this.iconHightSize];
        case "LightsOnlineOn":
          return [this.iconWidthSize, this.iconHightSize];
        case "LightsOnMaintenance":
          return [this.iconWidthSize, this.iconHightSize];

        default:
          return [this.iconSquareSize, this.iconSquareSize];
      }
    },
    setZoom(mode) {
      if (!this.$refs.clusterMap) return;
      const map = this.$refs.clusterMap.leafletObject;

      const addOrMin = mode === "add" ? 1 : -1;

      map.setZoom(map.getZoom() + addOrMin);
    },
    innerClick(data) {
      console.log(data);
      if (data.flag == "light") {
        if (data.icon === "LightsOnMaintenance") {
          this.$emit("on-maintenance-click", data);
          return;
        }
        window.location.href =
          "/V2/lightpage?id=" + data.id + "&clusterId=" + data.clusterID;
        // this.$router.push("/light?id=" + data.id + "&clusterId=" + data.clusterID);
      }
      if (data.flag == "lux") {
        // window.location.href = "/lux?id=" + data.id;
        this.$router.push("/V2/luxpage?id=" + data.id);
      }
    },
    async fitMapToMarkers() {
      if (this.markers && this.markers.length && this.mapReady) {
        const bounds = L.latLngBounds(
          this.markers.map((marker) => marker.latLng)
        );
        if (!this.$refs.clusterMap) return;
        const map = this.$refs.clusterMap.leafletObject;
        if (map) {
          console.log(map.fitBounds(bounds));
          map.invalidateSize(); // Recalculate the map size
          map.fitBounds(bounds);
        } else {
          console.error("Map object not found");
        }
      }
    },
    onMapReady() {
      this.mapReady = true;
      this.fitMapToMarkers();
    },
    handleMapShown() {
      // Ensure the map object is available
      if (!this.$refs.clusterMap) return;
      const map = this.$refs.clusterMap.leafletObject;
      if (map) {
        map.invalidateSize(); // Recalculate the map size
        this.fitMapToMarkers(); // Refit map bounds to markers
      } else {
        console.error("Map object is not ready yet");
      }
    },
  },
  mounted() {
    setTimeout(() => {
      this.handleMapShown();
    }, 1500);
    new Tooltip(document.body, {
      selector: "[data-bs-toggle='tooltip']",
    });
  },
  unmounted() {
    let popovers = document.getElementsByClassName("tooltip");
    popovers.forEach((item) => {
      item.remove();
    });
  },
};
</script>